import React, { useEffect, useRef, useState } from "react";

import { IconArrowToTop, IconCheck, IconCopy } from "~/components/icon";
import { cv, cx } from "~/tools/class-variance";
import { copyToClipboard } from "~/tools/copy-to-clipboard";
import { secondsToMilliseconds } from "~/tools/date-time";
import { onTimeout } from "~/tools/on-timeout";

import { AssistantAvatar } from "./_assistant-avatar";
import { UserAvatar } from "./_user-avatar";

const bubbleVariants = cv("group/bubble relative max-w-[85%] rounded-xl p-3 text-sm leading-5.25", {
	variants: {
		variant: {
			system: "border border-gray-300 bg-white text-gray-900 shadow-tooltip",
			user: "border border-gray-300 bg-gray-200 text-gray-900 shadow-tooltip",
			error: "border border-red-300 bg-red-100 text-red-700 shadow-tooltip",
		},
	},
});

const COPY_FEEDBACK_MS = secondsToMilliseconds(2);

const CopyButton: React.FC<{ contentRef: React.RefObject<HTMLDivElement | null>; copyText?: string }> = ({
	contentRef,
	copyText,
}) => {
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		if (!copied) {
			return;
		}
		return onTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
	}, [copied]);

	const handleCopy = () => {
		if (copyText != null) {
			const [failure] = copyToClipboard(copyText);
			if (failure == null) {
				setCopied(true);
			}
			return;
		}

		const el = contentRef.current;
		if (!el) {
			return;
		}
		const clone = el.cloneNode(true);
		if (!(clone instanceof HTMLElement)) {
			return;
		}
		for (const excluded of clone.querySelectorAll("[data-exclude-copy]")) {
			excluded.remove();
		}
		const text = clone.innerText;
		if (text) {
			const [failure] = copyToClipboard(text);
			if (failure == null) {
				setCopied(true);
			}
		}
	};

	return (
		<button
			aria-label="Copy message"
			className="absolute -top-2 -right-2 flex size-6 cursor-pointer items-center justify-center rounded-full border border-gray-300 bg-white opacity-0 shadow-tooltip transition-opacity duration-200 focus-visible:opacity-100 group-hover/bubble:opacity-100"
			onClick={handleCopy}
			type="button"
		>
			{copied ? <IconCheck className="size-3 text-primary-600" /> : <IconCopy className="size-3 text-gray-500" />}
		</button>
	);
};

const ResendButton: React.FC<{ onResend: () => void }> = ({ onResend }) => (
	<button
		aria-label="Resend message"
		className="absolute -right-2 -bottom-2 flex size-6 cursor-pointer items-center justify-center rounded-full border border-gray-300 bg-white opacity-0 shadow-tooltip transition-opacity duration-200 focus-visible:opacity-100 group-hover/bubble:opacity-100"
		onClick={onResend}
		type="button"
	>
		<IconArrowToTop className="size-3 text-gray-500" />
	</button>
);

export const ChatBubble: React.FC<{
	variant: "system" | "user" | "error";
	className?: string;
	copyable?: boolean;
	copyText?: string;
	onResend?: () => void;
	children: React.ReactNode;
}> = ({ variant, className, copyable = false, copyText, onResend, children }) => {
	const contentRef = useRef<HTMLDivElement>(null);

	return (
		<div className={cx("flex gap-2", variant === "user" && "flex-row-reverse")}>
			{variant === "user" ? <UserAvatar /> : <AssistantAvatar />}
			<div className={bubbleVariants({ variant, className })} ref={contentRef}>
				{children}
				{copyable && <CopyButton contentRef={contentRef} copyText={copyText} />}
				{onResend && <ResendButton onResend={onResend} />}
			</div>
		</div>
	);
};
